import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaFileAlt, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import materialRegisterService from "../../../../../services/web/material-register-service.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import useFetchData from "../../hooks/use-fetch-options.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import AggridUtils from "../../../../../utils/aggrid-utils.js";

const initialFilter = {
    material: "",
    group: "",
}

const MaterialRegistersList = () => {
    const navigate = navigateHook();
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);
    const {groupsOptions} = useFetchData({fetchGroups: true});

    const columnDefs = useMemo(() => [
        {field: "id", hide: true},
        { field: "date", headerName: "Data/hora", width: 130, comparator: AggridUtils.agGridCompareDateTime},
        { field: "storage", headerName: "Depósito", flex: 1, comparator: aggridUtils.lowerUpCaseComparator},
        { field: "group", headerName: "Grupo", flex: 1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator},
        { field: "material", headerName: "Material", flex: 1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator},
        { field: "quantity", headerName: "Quantidade", width: 120},
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const { data: registers = [], isLoading } = useQuery({
        queryKey: ['materials-registers'],
        queryFn: async () => {
            const { data } = await materialRegisterService.list();
            return data;
        },
    });

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            if (field === "group") value = value.label
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const onRowClicked = ({event, data}) => {
        const id = data.id;
        const urlDev = `/materials/registers/form/${id}`;
        let isBlank = false
        if (event.ctrlKey) isBlank = true
        navigate(urlDev, isBlank)
    }

    const openForm = () => {
        const urlDev = `/materials/registers/form`;
        navigate(urlDev)
    }

    const openReport = () => {
        const url = `/reports/material_movements?operation=acquisition`;
        navigate(url, true)
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Aquisições
                </Template.Title>
                <div className={"flex gap-2"}>
                    <Button onClick={openReport}>
                        <FaFileAlt/><span className="font-bold">Relatório</span>
                    </Button>
                    <Button onClick={openForm}>
                        <FaPlus/><span className="font-bold">Adicionar aquisição</span>
                    </Button>
                </div>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className="col-span-4">
                                <SelectComponent
                                    id="group"
                                    value={filter.group}
                                    options={groupsOptions || []}
                                    label={"Grupo"}
                                    onChange={(selected) => setFilter(prevState => ({...prevState, group: selected}))}
                                />
                            </div>
                            <div className="col-span-8 flex gap-4">
                                <InputForm id="material" name="material" value={filter.material} onChange={onChange}
                                           label="Material" classNameWrapper={"w-full"}/>
                                <div className="flex justify-start gap-2">
                                    <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={registers || []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
};

export default MaterialRegistersList;